import type { MeStore } from "@khazzin/api-client";

/**
 * The Store Portal's thirteen sections (`01/06` §3.2, EP10-S02), in menu
 * order, and the rules that turn one store's grid into a menu.
 *
 * **The grid is the only source.** A section is on the menu when the grid says
 * `read` or `write` for it, and nowhere else: never from a role name held
 * locally, never from a guess while the grid is loading. The server answers a
 * hidden section `404`, and the menu must never offer a door that answers that.
 */

export type SectionKey =
	| "dashboard"
	| "orders"
	| "external_import"
	| "new_order"
	| "inventory"
	| "catalog"
	| "returns"
	| "finance"
	| "delivery_rates"
	| "analytics"
	| "integrations"
	| "team"
	| "settings";

export const SECTIONS: readonly SectionKey[] = [
	"dashboard",
	"orders",
	"external_import",
	"new_order",
	"inventory",
	"catalog",
	"returns",
	"finance",
	"delivery_rates",
	"analytics",
	"integrations",
	"team",
	"settings",
];

export type Level = "none" | "read" | "write";

/** The account's level in one section of the store; absent is `none`. */
export function levelOf(store: MeStore | null, section: SectionKey): Level {
	const level = store?.access[section];
	return level === "read" || level === "write" ? level : "none";
}

/** Whether the level reaches what is asked: `write` includes `read`. */
export function reaches(level: Level, required: Exclude<Level, "none">): boolean {
	return required === "read" ? level !== "none" : level === "write";
}

/**
 * The service a section is for (call note 2, 2026-10-09): a store that
 * switched it off in Settings → Services no longer sees the section. New order
 * is fulfilment, which the warehouse refuses every order without.
 */
export const SECTION_SERVICE: Partial<Record<SectionKey, string>> = {
	new_order: "FULFILLMENT",
	returns: "RETURNS",
	delivery_rates: "DELIVERY",
};

/** Whether a section's service is switched off for the store. */
export function sectionOff(key: SectionKey, serviceOff: (code: string) => boolean): boolean {
	const service = SECTION_SERVICE[key];
	return service !== undefined && serviceOff(service);
}

/**
 * The menu: every section the grid shows at `read` or better, in order, less
 * any whose service the store switched off.
 */
export function visibleSections(
	store: MeStore | null,
	serviceOff: (code: string) => boolean = () => false,
): SectionKey[] {
	return SECTIONS.filter((key) => levelOf(store, key) !== "none" && !sectionOff(key, serviceOff));
}

/**
 * The phone's bottom bar (DA-10-01): Orders, Inventory, New Order when the
 * account may write it, and More — each only if the grid shows it.
 */
export function bottomBar(
	store: MeStore | null,
	serviceOff: (code: string) => boolean = () => false,
): (SectionKey | "more")[] {
	const out: (SectionKey | "more")[] = [];
	for (const key of ["orders", "inventory"] as const) {
		if (levelOf(store, key) !== "none") {
			out.push(key);
		}
	}
	if (levelOf(store, "new_order") === "write" && !sectionOff("new_order", serviceOff)) {
		out.push("new_order");
	}
	out.push("more");
	return out;
}

/** The URL path of a section. */
export function sectionPath(key: SectionKey): string {
	return `/${key.replaceAll("_", "-")}`;
}

const ACTIVE_STORE_KEY = "khazzin.store.active";

/** The store last chosen in this browser; storage can be denied, so it is best effort. */
export function rememberedStore(): string | null {
	try {
		return window.localStorage.getItem(ACTIVE_STORE_KEY);
	} catch {
		return null;
	}
}

export function rememberStore(storeId: string) {
	try {
		window.localStorage.setItem(ACTIVE_STORE_KEY, storeId);
	} catch {
		// A private window: the choice lasts until the tab closes.
	}
}

/** The store to open on: the remembered one while it is still ours, else the first. */
export function pickStore(stores: readonly MeStore[], remembered: string | null): MeStore | null {
	return stores.find((s) => s.storeId === remembered) ?? stores[0] ?? null;
}
